import $ from 'cash-dom';
import Cookie from 'js-cookie';
import { request, apiBase } from '@common/request';
import gaSC from '@common/utils/ga';

/**
 * Header sign-in — Google Identity Services, as the International app does it.
 *
 * THE WHOLE EXCHANGE IS THE BROWSER'S. Google issues a signed ID token to the
 * page, the page posts it to the gateway's `/auth/google-login`, and the
 * gateway answers with the session. Nothing server-side is involved, which is
 * why there is no `login/googlelogin` route here — that belongs to the
 * authorization-code flow the .in engine runs, and that flow needs a client
 * secret this project does not hold.
 *
 * THE SCRIPT IS LOADED ON DEMAND. `accounts.google.com/gsi/client` is fetched
 * the first time the popup opens, never on page load. It is the one
 * third-party UI dependency in this bundle (CLAUDE.md principle 5 lists the
 * lazy Firebase push chunk as the other), and keeping it behind the click is
 * what stops it costing every visitor who never signs in.
 *
 * THE COOKIES ARE THE NEXT.JS APP'S: `user` (JSON), `access_token`,
 * `refresh_token`, `token_expiry` — same names, same domain, so a visitor
 * already signed in when a country cuts over stays signed in. The .in names
 * (`twentyfour`, `goim`, `at`, …) belong to a different backend and mean
 * nothing here.
 */

const GSI_SRC = 'https://accounts.google.com/gsi/client';
const AUTH_COOKIES = ['access_token', 'refresh_token', 'token_expiry', 'user'];
/** Matches the Next.js token-manager, which writes 365-day cookies. */
const COOKIE_DAYS = 365;

let gsiPromise = null;

function config() {
  return window.goGlobal || {};
}

function webOrigin() {
  const configured = String(config().WEB_URL || '').replace(/\/+$/, '');
  return configured || window.location.origin;
}

/**
 * SITE-RELATIVE, NOT THE CDN. `/v5/user-default.jpg` lives on India's CDN and
 * 404s on every other country's — and a 404 here is not cosmetic, because it is
 * the fallback the error handler swaps in. The file is served from this app's
 * own public/, the same path and the same asset the Next.js app uses, so each
 * country serves its own copy and there is nothing to get wrong per geo.
 */
function defaultAvatar() {
  return '/assets/images/default-avatar.svg';
}

/**
 * The signed-in user, or null.
 *
 * EXPORTED SO NOTHING ELSE NAMES A COOKIE. Every login gate in the migrated .in
 * bundles tested `Cookie.get('twentyfour')` — India's session cookie, which
 * this backend never sets. Signed in or not, that read was undefined, so the
 * like button, follow and the comment boxes all opened the login popup at a
 * visitor who was already logged in. One place knows the cookie names now.
 */
export function currentUser() {
  const raw = Cookie.get('user');
  if (!raw) return null;
  try {
    const user = JSON.parse(raw);
    return user && (user.name || user.email) ? user : null;
  } catch {
    // A cookie we cannot parse is not a session. Drop it rather than leaving
    // the header half-signed-in on every future page.
    Cookie.remove('user', { path: '/' });
    return null;
  }
}

/** Is there a session? What every "do I need to prompt?" check should ask. */
export function isLoggedIn() {
  return !!currentUser();
}

/** The display name, for "Commenting as …" style copy. */
export function currentUserName() {
  const user = currentUser();
  return (user && (user.name || user.email)) || '';
}

/** Mirrors the Next.js storeAuthData, cookie for cookie. */
function storeAuthData(result) {
  const opts = { path: '/', expires: COOKIE_DAYS, sameSite: 'lax' };
  const expiry = Date.now() + Number(result.expiresIn || 0) * 1000;
  Cookie.set('access_token', result.accessToken, opts);
  Cookie.set('refresh_token', result.refreshToken, opts);
  Cookie.set('token_expiry', String(expiry), opts);
  Cookie.set('user', JSON.stringify(result.user || {}), opts);
}

function clearAuthData() {
  AUTH_COOKIES.forEach((name) => Cookie.remove(name, { path: '/' }));
}

/** Load gsi/client once. Resolves to window.google, or rejects. */
function loadGsi() {
  if (window.google && window.google.accounts) return Promise.resolve(window.google);
  if (gsiPromise) return gsiPromise;

  gsiPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () =>
      window.google && window.google.accounts
        ? resolve(window.google)
        : reject(new Error('gsi loaded without accounts'));
    script.onerror = () => {
      // Let a later click try again rather than caching the failure forever.
      gsiPromise = null;
      reject(new Error('gsi failed to load'));
    };
    document.head.appendChild(script);
  });
  return gsiPromise;
}

function showError(message) {
  const el = $('#loginModal [data-type="login-error"]');
  if (!el.length) return;
  el.text(message).removeAttr('hidden');
}

/** The credential callback: ID token → gateway session → signed-in header. */
async function onCredential(response) {
  const idToken = response && response.credential;
  if (!idToken) {
    showError('Failed to get credentials from Google. Please try again.');
    return;
  }

  const result = await request.post(
    apiBase() + '/auth/google-login',
    { idToken },
    { throwErrors: false },
  );

  if (!result || !result.accessToken || !result.user) {
    showError('Failed to authenticate with Google. Please try again.');
    return;
  }

  gaSC('Login', 'Google');
  storeAuthData(result);
  closeModal();
  // Reload rather than patching the header in place: a signed-in visitor sees
  // different content in several places, and the generated page was written
  // for the signed-out one.
  window.location.reload();
}

/** Ask Google to render its own button into the popup's slot. */
async function renderGoogleButton() {
  const slot = document.getElementById('gGoogleBtn');
  if (!slot || slot.getAttribute('data-state') === 'ready') return;

  const clientId = config().GOOGLE_CLIENT_ID;
  if (!clientId) return;

  let google;
  try {
    google = await loadGsi();
  } catch {
    showError('Could not reach Google. Please try again.');
    return;
  }

  google.accounts.id.initialize({ client_id: clientId, callback: onCredential });
  google.accounts.id.renderButton(slot, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'continue_with',
    shape: 'rectangular',
    logo_alignment: 'center',
    width: slot.clientWidth || 280,
  });
  slot.setAttribute('data-state', 'ready');
}

export async function openLoginModal() {
  if ($('#loginModal').length === 0) {
    const html = await request.get(webOrigin() + '/login/');
    if (!html) return;
    $('#gBody').append(html);
  }

  $('#gBody').addClass('f-open');
  $('#overlay').one('click', closeModal);
  // After the markup is in the document — renderButton needs a real element.
  renderGoogleButton();
}

function closeModal() {
  $('#overlay').off('click', closeModal);
  $('#loginModal').remove();
  $('#gBody').removeClass('f-open');
}

/** Swap the Login button for the profile chip. */
function renderSignedIn(user) {
  const avatar = user.avatar || defaultAvatar();
  const name = user.name || user.email || '';
  const profileUrl = webOrigin() + '/profile/';

  $('.g-login .loginContainer').remove();
  $('#gLoginButton, #mLoginButton').remove();

  $('.g-login').append(`
    <div class="gprof" id="profDropDown" style="display: inline-block">
      <span><img src="${avatar}" alt="User Profile" /></span>
    </div>
    <div class="gprof-dropdown">
      <a href="${profileUrl}">View Profile</a>
      <a class="go-logout" href="javascript:void(0);">Logout</a>
    </div>
  `);

  $('.g-login-flex').html(`
    <div class="logged-in">
      <div class="logged-in-flex">
        <a href="${profileUrl}" class="g-logged-in-img"><img src="${avatar}" alt="User Profile" /></a>
        <p class="name">${name}</p>
      </div>
      <span class="go-logout" role="button" tabindex="0" aria-label="Logout"></span>
    </div>
  `);

  // The avatar URL comes from Google and can 404 once it rotates, so fall back
  // to the local default.
  //
  // THE GUARD IS A FLAG, NOT `this.onerror = null`. This listener is attached
  // with .on(), i.e. addEventListener, and clearing the `onerror` PROPERTY does
  // not remove it — so if the fallback itself fails to load, the handler fires
  // again, reassigns the same src, and the browser requests it forever. That is
  // exactly what happened while the fallback pointed at India's CDN. A flag on
  // the element cannot be defeated by either failure.
  $('.gprof img, .g-logged-in-img img').on('error', function () {
    if (this.getAttribute('data-fallback') === 'true') return;
    this.setAttribute('data-fallback', 'true');
    this.src = defaultAvatar();
  });
}

async function logout() {
  const refreshToken = Cookie.get('refresh_token');
  const accessToken = Cookie.get('access_token');

  // Tell the gateway to revoke, THEN clear. Best effort: a visitor who clicked
  // Logout must end up signed out of this browser even if the call fails —
  // keeping the cookies because a request errored is the one indefensible
  // outcome. request.post swallows failures unless asked not to.
  if (refreshToken) {
    await request.post(
      apiBase() + '/auth/logout',
      { refreshToken },
      { bearer: accessToken, throwErrors: false },
    );
  }

  clearAuthData();
  window.location.href = webOrigin() + '/';
}

export function loginInit() {
  $('#gBody').on(
    'click',
    '.loginContainer, #gLoginButton, #mLoginButton, #gLoginGoogle',
    function () {
      gaSC('Login Button Click');
      if (!currentUser()) openLoginModal();
    },
  );

  $('#gBody').on('click', '#closeModal', closeModal);

  const user = currentUser();
  if (!user) return;

  renderSignedIn(user);
  $('#gBody').on('click', '.go-logout', logout);
}
